import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function OrderReceiptButton({ orderId, orderNumber, compact = false }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState("");
  const urlRef = useRef("");

  const openPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/my/orders/${orderId}/receipt`, { responseType: "blob" });
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      urlRef.current = url;
      setBlobUrl(url);
      setOpen(true);
    } catch (error) {
      toast.error("Impossible de générer le reçu PDF");
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `recu-${orderNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Téléchargement du reçu PDF lancé");
  };

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  return (
    <>
      <button
        data-testid={compact ? `receipt-pdf-btn-${orderNumber}` : "receipt-pdf-btn"}
        onClick={openPreview}
        disabled={loading}
        className={compact
          ? "flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-[11px] font-bold text-ink hover:border-brand hover:text-brand disabled:opacity-60"
          : "flex items-center gap-2 rounded-lg bg-brand px-3.5 py-2 text-xs font-bold text-white hover:bg-brand/90 disabled:opacity-60"}
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
        {compact ? "PDF" : "Reçu PDF"}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl gap-0 p-0" data-testid="receipt-preview-dialog">
          <DialogHeader className="border-b border-line px-5 py-4">
            <DialogTitle className="font-display text-base font-extrabold text-ink">
              Reçu de la commande {orderNumber}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Vérifiez votre reçu avant de lancer le téléchargement.
            </DialogDescription>
          </DialogHeader>
          {blobUrl && (
            <iframe
              title={`Reçu ${orderNumber}`}
              src={`${blobUrl}#toolbar=0`}
              className="h-[62vh] w-full bg-slate-100"
              data-testid="receipt-preview-iframe"
            />
          )}
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-line px-5 py-4">
            <button
              data-testid="receipt-close-btn"
              onClick={() => setOpen(false)}
              className="rounded-full border border-line px-5 py-2.5 text-xs font-bold text-ink hover:border-brand hover:text-brand"
            >
              Fermer
            </button>
            <button
              data-testid="receipt-download-btn"
              onClick={download}
              className="flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-xs font-bold text-white hover:bg-brand/90"
            >
              <Download className="h-4 w-4" /> Télécharger le PDF
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
